import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { paymentsAPI } from '../lib/api';

// Handles the return from Stripe's hosted checkout.
//  /payment/success?session_id=...  → polls status until the payment is confirmed
//  /payment/cancel                  → shows a friendly "cancelled" message
const MAX_POLLS = 12;      // ~24s at 2s intervals
const POLL_INTERVAL = 2000;

export default function PaymentReturnPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const cancelled = location.pathname.endsWith('/cancel');

  const [state, setState] = useState(cancelled ? 'cancelled' : 'checking'); // checking | paid | timeout | error | cancelled
  const pollsRef = useRef(0);
  const timerRef = useRef(null);

  const poll = useCallback(async () => {
    if (!sessionId) { setState('error'); return; }
    try {
      const { data } = await paymentsAPI.stripeStatus(sessionId);
      if (data.payment_status === 'paid') { setState('paid'); return; }
    } catch (e) { /* keep polling; transient */ }
    pollsRef.current += 1;
    if (pollsRef.current >= MAX_POLLS) { setState('timeout'); return; }
    timerRef.current = setTimeout(poll, POLL_INTERVAL);
  }, [sessionId]);

  useEffect(() => {
    if (cancelled) return;
    poll();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [cancelled, poll]);

  const wrap = { minHeight: '100vh', background: '#F1EAE1', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' };
  const card = { background: '#fff', border: '1px solid #ECE7DE', borderRadius: 16, padding: '3rem', textAlign: 'center', maxWidth: 480, width: '100%' };

  if (state === 'checking') {
    return (
      <div style={wrap} data-testid="payment-checking">
        <Card style={card}>
          <div className="riq-spin" style={{ width: 44, height: 44, border: '3px solid #F0EBE3', borderTopColor: '#0076A3', borderRadius: '50%', margin: '0 auto 1.5rem', animation: 'riqspin 0.8s linear infinite' }} />
          <style>{`@keyframes riqspin{to{transform:rotate(360deg)}}`}</style>
          <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.35rem', fontWeight: 700, color: '#0076A3', marginBottom: '0.5rem' }}>Confirming your payment…</h2>
          <p style={{ fontSize: '0.9375rem', color: '#718096' }}>This only takes a moment. Please don't close this window.</p>
        </Card>
      </div>
    );
  }

  if (state === 'paid') {
    return (
      <div style={wrap} data-testid="payment-success">
        <Card style={card}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
          </div>
          <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.5rem', fontWeight: 700, color: '#0076A3', marginBottom: '0.5rem' }}>Payment Successful!</h2>
          <p style={{ fontSize: '0.9375rem', color: '#718096', marginBottom: '2rem' }}>Your purchase is confirmed and your account has been updated.</p>
          <div style={{ display: 'flex', gap: '0.75rem', flexDirection: 'column' }}>
            <Button data-testid="return-dashboard-btn" onClick={() => navigate('/dashboard')} style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontWeight: 600, fontSize: '1rem', padding: '0.75rem 2.5rem', width: '100%' }}>Go to Dashboard</Button>
            <Button data-testid="return-billing-btn" onClick={() => navigate('/billing')} variant="outline" style={{ borderRadius: 999, fontWeight: 600, width: '100%' }}>View Billing</Button>
          </div>
        </Card>
      </div>
    );
  }

  if (state === 'cancelled') {
    return (
      <div style={wrap} data-testid="payment-cancelled">
        <Card style={card}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </div>
          <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.5rem', fontWeight: 700, color: '#0076A3', marginBottom: '0.5rem' }}>Payment Cancelled</h2>
          <p style={{ fontSize: '0.9375rem', color: '#718096', marginBottom: '2rem' }}>No charge was made. You can pick up where you left off whenever you're ready.</p>
          <div style={{ display: 'flex', gap: '0.75rem', flexDirection: 'column' }}>
            <Button data-testid="cancel-pricing-btn" onClick={() => navigate('/pricing')} style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontWeight: 600, width: '100%' }}>Back to Pricing</Button>
            <Button data-testid="cancel-billing-btn" onClick={() => navigate('/billing')} variant="outline" style={{ borderRadius: 999, fontWeight: 600, width: '100%' }}>Go to Billing</Button>
          </div>
        </Card>
      </div>
    );
  }

  // timeout | error
  return (
    <div style={wrap} data-testid="payment-pending">
      <Card style={card}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#FFFBEB', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="6" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
        </div>
        <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.35rem', fontWeight: 700, color: '#0076A3', marginBottom: '0.5rem' }}>Still processing</h2>
        <p style={{ fontSize: '0.9375rem', color: '#718096', marginBottom: '2rem' }}>Your payment is taking a little longer than usual to confirm. If you completed checkout, your account will update shortly — check your Billing page in a minute.</p>
        <Button data-testid="pending-billing-btn" onClick={() => navigate('/billing')} style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontWeight: 600, width: '100%' }}>Go to Billing</Button>
      </Card>
    </div>
  );
}
